import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { STATIC_PAGES } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import "../styles/PublicCommon.css";
import "./ContactPage.css";

const LINE_URL = "https://line.me/R/ti/p/@703jwzlx?from=page&accountId=703jwzlx";

// 有効なお問い合わせ種類
const VALID_SUBJECTS = ["施工に関して", "採用に関して", "その他ご相談"];

// アイコンコンポーネント
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LineIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="alert-icon" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const ErrorCircleIcon = () => (
  <svg className="alert-icon" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

export const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get("subject");

  // クエリパラメータが有効な値の場合はそれを初期値として使用
  const initialSubject = subjectParam && VALID_SUBJECTS.includes(subjectParam)
    ? subjectParam
    : "施工に関して";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: initialSubject,
    message: "",
  });
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [isPrivacyPopupOpen, setIsPrivacyPopupOpen] = useState(false);

  // URLパラメータが変更されたら subject を更新
  useEffect(() => {
    if (subjectParam && VALID_SUBJECTS.includes(subjectParam)) {
      setFormData(prev => ({ ...prev, subject: subjectParam }));
    }
  }, [subjectParam]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [isLinePopupOpen, setIsLinePopupOpen] = useState(false);
  const formSectionRef = useRef<HTMLElement>(null);
  const submitMessageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitStatus === "success" || submitStatus === "error") {
      submitMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [submitStatus]);

  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  const handleLineClick = useCallback(() => {
    if (isMobileDevice()) {
      window.open(LINE_URL, "_blank", "noopener,noreferrer");
    } else {
      setIsLinePopupOpen(true);
    }
  }, [isMobileDevice]);

  const handlePhoneCardClick = useCallback(() => {
    window.location.href = "tel:0894-62-4511";
  }, []);

  const handleFormCardClick = useCallback(() => {
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleCardKeyDown = useCallback(
    (handler: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
      }
    },
    []
  );

  const closeLinePopup = useCallback(() => {
    setIsLinePopupOpen(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "お名前を入力してください";
    }

    if (!formData.email.trim()) {
      newErrors.email = "メールアドレスを入力してください";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "有効なメールアドレスを入力してください";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "電話番号を入力してください";
    }

    if (!formData.message.trim()) {
      newErrors.message = "お問い合わせ内容を入力してください";
    }

    if (!privacyAgreed) {
      newErrors.privacy = "プライバシーポリシーへの同意が必要です";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        let serverMessage = "";
        try {
          const errBody = await response.json();
          serverMessage = errBody?.error ?? "";
        } catch {
          // ignore parse failure
        }
        throw new Error(serverMessage || `HTTP ${response.status}`);
      }

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "施工に関して",
        message: "",
      });
      setPrivacyAgreed(false);
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead page={STATIC_PAGES.contact} />

      <div className="public-page">
        <PublicHeader activePage="contact" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* ページヘッダー */}
          <section
            className="page-header"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(26,158,150,0.78) 0%, rgba(13,120,114,0.78) 50%, rgba(21,138,130,0.78) 100%), url('/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E7%A4%BE%E5%86%85%E7%8E%84%E9%96%A2.JPG')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="page-header-overlay" />
            <div className="page-header-content">
              <h1 className="page-title">お問い合わせ</h1>
              <p className="page-subtitle">Contact</p>
            </div>
          </section>

          {/* イントロセクション */}
          <section className="contact-intro-section">
            <div className="container">
              <p className="contact-lead-text">
                お見積もり・ご相談は<strong>無料</strong>です。<br />
                住宅のリフォームから土木工事まで、どんな小さなことでもお気軽にご相談ください。<br />
                専門スタッフが丁寧にご対応いたします。
              </p>
            </div>
          </section>

          {/* お問い合わせ方法セクション */}
          <section className="contact-methods-section">
            <div className="container">
              <h2 className="section-title">お問い合わせ方法</h2>
              <p className="section-description">
                下記のいずれかの方法でお問い合わせください。
              </p>

              <div className="methods-grid">
                <div
                  className="method-card method-card-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={handlePhoneCardClick}
                  onKeyDown={handleCardKeyDown(handlePhoneCardClick)}
                  aria-label="お電話でお問い合わせ：0894-62-4511"
                >
                  <div className="method-icon-wrapper">
                    <PhoneIcon />
                  </div>
                  <h3>お電話でのお問い合わせ</h3>
                  <a
                    href="tel:0894-62-4511"
                    className="phone-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    0894-62-4511
                  </a>
                  <p className="method-hours">受付時間：平日 9:00〜17:00</p>
                </div>

                <div
                  className="method-card method-card-clickable"
                  role="button"
                  tabIndex={0}
                  data-track="line"
                  onClick={handleLineClick}
                  onKeyDown={handleCardKeyDown(handleLineClick)}
                  aria-label="LINEで相談する"
                >
                  <div className="method-icon-wrapper">
                    <LineIcon />
                  </div>
                  <h3>LINEでのお問い合わせ</h3>
                  <p className="method-info">写真を送って相談できます</p>
                  <button
                    type="button"
                    className="line-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLineClick();
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                    </svg>
                    LINEで相談する
                  </button>
                </div>

                <div
                  className="method-card method-card-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={handleFormCardClick}
                  onKeyDown={handleCardKeyDown(handleFormCardClick)}
                  aria-label="お問い合わせフォームへ移動"
                >
                  <div className="method-icon-wrapper">
                    <MailIcon />
                  </div>
                  <h3>フォームでのお問い合わせ</h3>
                  <p className="method-info">24時間受付</p>
                  <p className="method-detail">下記フォームよりお送りください</p>
                </div>
              </div>
            </div>
          </section>

          {/* お問い合わせフォームセクション */}
          <section className="form-section" ref={formSectionRef}>
            <div className="container-narrow">
              <h2 className="section-title">お問い合わせフォーム</h2>

              <div ref={submitMessageRef} />

              {submitStatus === "success" && (
                <div className="alert alert-success" role="alert">
                  <CheckCircleIcon />
                  <div className="alert-content">
                    <p><strong>お問い合わせありがとうございます。</strong></p>
                    <p>内容を確認の上、担当者よりご連絡させていただきます。</p>
                  </div>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="alert alert-error" role="alert">
                  <ErrorCircleIcon />
                  <div className="alert-content">
                    <p><strong>送信中にエラーが発生しました。</strong></p>
                    <p>お手数ですが、お電話またはLINEにてお問い合わせください。</p>
                  </div>
                </div>
              )}

              {/* 感謝メッセージ */}
              <div className="form-thank-you-banner">
                <p className="form-thank-you-text">
                  お問い合わせいただきまして<strong>ありがとうございます</strong>
                </p>
                <p className="form-thank-you-response">
                  <span className="response-time-badge">2営業日以内</span>にご連絡差し上げます
                </p>
                <p className="form-notice-sub">
                  ※ 営業メールはお断りしております
                </p>
              </div>

              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    お名前
                    <span className="required-badge">必須</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${errors.name ? "error" : ""}`}
                    placeholder="例：山田 太郎"
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                  />
                  {errors.name && (
                    <p id="name-error" className="error-message" role="alert">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    電話番号
                    <span className="required-badge">必須</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? "error" : ""}`}
                    placeholder="例：0894-62-4511"
                    aria-required="true"
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "phone-error" : undefined}
                  />
                  {errors.phone && (
                    <p id="phone-error" className="error-message" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    メールアドレス
                    <span className="required-badge">必須</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? "error" : ""}`}
                    placeholder="例：example@email.com"
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="error-message" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <fieldset className="radio-fieldset">
                    <legend className="form-label">
                      お問い合わせ種類
                      <span className="required-badge">必須</span>
                    </legend>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="subject"
                          value="施工に関して"
                          checked={formData.subject === "施工に関して"}
                          onChange={handleChange}
                          className="radio-input"
                        />
                        <span className="radio-text">施工に関して</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="subject"
                          value="採用に関して"
                          checked={formData.subject === "採用に関して"}
                          onChange={handleChange}
                          className="radio-input"
                        />
                        <span className="radio-text">採用に関して</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="subject"
                          value="その他ご相談"
                          checked={formData.subject === "その他ご相談"}
                          onChange={handleChange}
                          className="radio-input"
                        />
                        <span className="radio-text">その他ご相談</span>
                      </label>
                    </div>
                  </fieldset>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    お問い合わせ内容
                    <span className="required-badge">必須</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className={`form-textarea ${errors.message ? "error" : ""}`}
                    placeholder="ご相談内容をご記入ください"
                    aria-required="true"
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "message-error" : undefined}
                  />
                  {errors.message && (
                    <p id="message-error" className="error-message" role="alert">
                      {errors.message}
                    </p>
                  )}
                </div>

                <div className="form-privacy-agreement">
                  <label className={`privacy-checkbox-label ${errors.privacy ? "error" : ""}`}>
                    <input
                      type="checkbox"
                      checked={privacyAgreed}
                      onChange={(e) => {
                        setPrivacyAgreed(e.target.checked);
                        if (errors.privacy) {
                          setErrors(prev => ({ ...prev, privacy: "" }));
                        }
                      }}
                      className="privacy-checkbox"
                    />
                    <span className="privacy-checkbox-custom" />
                    <span className="privacy-text">
                      <button
                        type="button"
                        className="privacy-link-button"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsPrivacyPopupOpen(true);
                        }}
                      >
                        プライバシーポリシー
                      </button>
                      に同意する
                      <span className="required-badge">必須</span>
                    </span>
                  </label>
                  {errors.privacy && (
                    <p className="error-message" role="alert">
                      {errors.privacy}
                    </p>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    <span className="submit-button-text">
                      {isSubmitting ? (
                        <>
                          <span className="loading-spinner" />
                          送信中...
                        </>
                      ) : (
                        "送信する"
                      )}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* 補足情報セクション */}
          <section className="contact-note-section">
            <div className="container">
              <div className="note-box">
                <h3 className="note-title">
                  <InfoIcon />
                  お問い合わせにあたって
                </h3>
                <ul className="note-list">
                  <li>お問い合わせ内容によっては、回答までにお時間をいただく場合がございます。</li>
                  <li>土日祝日にいただいたお問い合わせは、翌営業日以降のご対応となります。</li>
                  <li>お急ぎの場合は、お電話でのお問い合わせをおすすめいたします。</li>
                </ul>
              </div>
            </div>
          </section>
        </main>

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small.png"
                srcSet="/static/miyako-logo-small.png 1x, /static/miyako-logo-2x.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </a>

              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <a href="/">ホーム</a>
              <a href="/about">会社概要</a>
              <a href="/services">業務内容</a>
              <a href="/works">施工実績</a>
              <a href="/recruit">採用情報</a>
              <a href="/contact">お問い合わせ</a>
            </nav>

            <p className="footer-copyright">
              <small>
                ©2024 都建設株式会社 Powered by{" "}
                <a href="#" target="_blank" rel="noopener noreferrer">
                  VERYWEBB
                </a>
              </small>
            </p>
          </div>
        </footer>

        {/* プライバシーポリシー ポップアップ */}
        {isPrivacyPopupOpen && (
          <>
            <div className="privacy-popup-overlay" onClick={() => setIsPrivacyPopupOpen(false)} aria-hidden="true" />
            <div className="privacy-popup" role="dialog" aria-modal="true" aria-labelledby="privacy-popup-title">
              <div className="privacy-popup-header">
                <h3 id="privacy-popup-title" className="privacy-popup-title">プライバシーポリシー</h3>
                <button
                  className="privacy-popup-close"
                  onClick={() => setIsPrivacyPopupOpen(false)}
                  aria-label="閉じる"
                  type="button"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              </div>
              <div className="privacy-popup-content">
                <p className="policy-intro-popup">
                  都建設株式会社（以下、「当社」といいます。）は、お客様の個人情報の保護を重要な責務と考え、
                  以下のとおりプライバシーポリシーを定め、適切に取り扱います。
                </p>

                <div className="policy-article-popup">
                  <h4>1. 個人情報の定義</h4>
                  <p>
                    個人情報とは、個人情報保護法第2条第1項により定義された、生存する個人に関する情報であって、
                    当該情報に含まれる氏名、生年月日その他の記述等により特定の個人を識別できるもの（他の情報と容易に照合することができ、
                    それにより特定の個人を識別することができることとなるものを含みます。）、もしくは個人識別符号が含まれる情報を指します。
                  </p>
                </div>

                <div className="policy-article-popup">
                  <h4>2. 個人情報の収集方法</h4>
                  <p>当社は、お客様が当社のサービスをご利用いただく際に、以下のような方法で個人情報を収集させていただく場合があります。</p>
                  <ul>
                    <li>お問い合わせフォームからのお問い合わせ時</li>
                    <li>お見積もり依頼時</li>
                    <li>採用応募時</li>
                    <li>電話、FAX、メール、LINEでのお問い合わせ時</li>
                    <li>当ウェブサイトの閲覧時（アクセス情報。詳しくは「7. アクセス情報の取得と利用」をご覧ください）</li>
                  </ul>
                </div>

                <div className="policy-article-popup">
                  <h4>3. 個人情報の利用目的</h4>
                  <p>当社は、収集した個人情報を以下の目的で利用いたします。</p>
                  <ul>
                    <li>お問い合わせへの対応</li>
                    <li>お見積もりの作成および提供</li>
                    <li>サービスの提供、維持、改善</li>
                    <li>採用選考</li>
                    <li>当ウェブサイトの利用状況の分析と改善、不正アクセス・攻撃の検知と防止</li>
                    <li>当社からのご案内、ご連絡</li>
                    <li>その他、上記利用目的に付随する目的</li>
                  </ul>
                </div>

                <div className="policy-article-popup">
                  <h4>4. 個人情報の第三者提供</h4>
                  <p>当社は、以下の場合を除き、お客様の個人情報を第三者に提供することはありません。</p>
                  <ul>
                    <li>お客様の同意がある場合</li>
                    <li>法令に基づく場合</li>
                    <li>人の生命、身体または財産の保護のために必要がある場合であって、お客様の同意を得ることが困難である場合</li>
                    <li>その他、個人情報保護法その他の法令で認められる場合</li>
                  </ul>
                </div>

                <div className="policy-article-popup">
                  <h4>5. 個人情報の管理</h4>
                  <p>
                    当社は、お客様の個人情報を正確かつ最新の状態に保ち、個人情報への不正アクセス・紛失・破損・改ざん・漏洩などを防止するため、
                    セキュリティシステムの維持・管理体制の整備等の必要な措置を講じます。
                  </p>
                </div>

                <div className="policy-article-popup">
                  <h4>6. 個人情報の開示・訂正・削除</h4>
                  <p>
                    お客様は、当社に対し、個人情報保護法の定めるところにより、
                    ご自身の個人情報の開示、訂正、追加、削除および利用停止を請求することができます。
                  </p>
                </div>

                <div className="policy-article-popup">
                  <h4>7. アクセス情報の取得と利用</h4>
                  <p>
                    当社は、当ウェブサイトの利用状況の把握・改善と、不正アクセス・攻撃の検知・防止のため、
                    IPアドレス、ブラウザ・端末の種類、閲覧したページと日時、参照元、ページ内でクリック・タップした位置、押したリンク・ボタン、
                    スクロールした量、お問い合わせフォームの送信の有無を、当社のサーバーに記録します。
                    外部の解析サービスには送信せず、Cookie等も使用しません。フォームの入力内容は記録しません。
                    IPアドレスとブラウザの種類は90日、そのほかは400日で削除し、閲覧できるのは当社のサーバー管理者に限ります。
                    ブラウザで「トラッキング拒否（Do Not Track）」または「Global Privacy Control」を有効にしている場合は記録しません。
                    詳しくは<a href="/privacy-policy#access-data" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>をご覧ください。
                  </p>
                </div>

                <div className="policy-article-popup">
                  <h4>8. 外部サービスへの情報の送信</h4>
                  <p>
                    当ウェブサイトは、文字の表示に Google Fonts、地図の表示に Google マップ（いずれも Google LLC）、
                    LINE公式アカウントのQRコードの表示に LINE（LINEヤフー株式会社）を利用しており、
                    表示の際にお客様のIPアドレス・ブラウザの種類・表示中のページのURLなどが各社に送信されます。
                    当社は一般公開ページでCookieを使用していませんが、これらの外部サービスが独自にCookieを使用する場合があります。
                  </p>
                </div>

                <div className="policy-article-popup">
                  <h4>9. プライバシーポリシーの変更</h4>
                  <p>
                    当社は、法令の変更等に伴い、予告なく本プライバシーポリシーを変更することがあります。
                    変更後のプライバシーポリシーは、当ウェブサイトに掲載した時点から効力を生じるものとします。
                  </p>
                </div>

                <div className="policy-article-popup">
                  <h4>10. お問い合わせ先</h4>
                  <p>個人情報の取扱いに関するお問い合わせは、以下までご連絡ください。</p>
                  <div className="contact-info-popup">
                    <p><strong>都建設株式会社</strong></p>
                    <p>〒797-0015 愛媛県西予市宇和町卯之町1丁目432</p>
                    <p>TEL: 0894-62-4511</p>
                    <p>受付時間：平日 9:00～17:00</p>
                  </div>
                </div>

                <p className="policy-date-popup">制定日：2024年1月1日／改定日：2026年10月31日</p>
              </div>
              <div className="privacy-popup-footer">
                <button
                  type="button"
                  className="privacy-agree-button"
                  onClick={() => {
                    setPrivacyAgreed(true);
                    setIsPrivacyPopupOpen(false);
                    if (errors.privacy) {
                      setErrors(prev => ({ ...prev, privacy: "" }));
                    }
                  }}
                >
                  同意する
                </button>
                <button
                  type="button"
                  className="privacy-close-button"
                  onClick={() => setIsPrivacyPopupOpen(false)}
                >
                  閉じる
                </button>
              </div>
            </div>
          </>
        )}

        {/* LINE公式アカウント ポップアップ */}
        {isLinePopupOpen && (
          <>
            <div className="line-popup-overlay" onClick={closeLinePopup} aria-hidden="true" />
            <div className="line-popup" role="dialog" aria-modal="true" aria-labelledby="line-popup-title">
              <button
                className="line-popup-close"
                onClick={closeLinePopup}
                aria-label="閉じる"
                type="button"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
              <div className="line-popup-content">
                <h3 id="line-popup-title" className="line-popup-title">LINE公式アカウント</h3>
                <p className="line-popup-description">
                  お気軽にお問い合わせください。<br />
                  QRコードを読み取るか、下のボタンから友だち追加できます。
                </p>
                <div className="line-popup-qr">
                  <img
                    src="https://qr-official.line.me/gs/M_703jwzlx_GW.png"
                    alt="LINE公式アカウント QRコード"
                    width="180"
                    height="180"
                  />
                </div>
                <a
                  href={LINE_URL}
                  className="line-popup-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      d="M12 2C6.48 2 2 5.82 2 10.49c0 4.21 3.74 7.74 8.79 8.41.34.07.8.23.92.52.1.27.07.68.03.95l-.15.89c-.05.27-.21 1.05.92.57 1.13-.48 6.09-3.58 8.3-6.14C22.21 13.91 22 12.29 22 10.49 22 5.82 17.52 2 12 2z"
                      fill="white"
                    />
                  </svg>
                  友だち追加
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ContactPage;
